// Croqui: `components/Btn.tsx` (Btn + IconBtn).
//
// WEB-133: o desenho é o do `components/ui/button.tsx` (pílula chapada), só a API é a do
// kit. Um seller não pode perceber que o admin usa outro kit. `primary` é a pílula de
// TINTA (nunca dourada), `card` é a `outline`, `danger` é a destrutiva neutra.
//
// O alvo de toque de 40px abaixo de `sm` (`spec/17` §7 item 12) vem do `buttonVariants`.

import type { MouseEvent, ReactNode } from 'react';
import { cn } from '../../lib/cn.ts';
import { buttonVariants } from '../ui/button.tsx';
import { Icon, type IconName } from './icon.tsx';

export type BtnKind = 'primary' | 'card' | 'ghost' | 'danger';

const KIND = {
  primary: 'default',
  card: 'outline',
  ghost: 'ghost',
  danger: 'destructive',
} as const satisfies Record<BtnKind, string>;

/** Botão: `primary` (tinta, um por tela), `card` (padrão), `ghost`, `danger`. */
export function Btn({
  kind = 'card',
  icon,
  children,
  small = false,
  type = 'button',
  onClick,
  disabled = false,
  className,
  ...rest
}: {
  kind?: BtnKind;
  icon?: IconName;
  children?: ReactNode;
  small?: boolean;
  type?: 'button' | 'submit' | 'reset';
  /** Recebe o evento: filtro que precisa de `stopPropagation` dentro de linha clicável. */
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
  className?: string;
  'aria-label'?: string;
  /** Id do `<form>` que o botão envia quando ele mora fora dele (rodapé fixo da gaveta). */
  form?: string;
  /** Botão de alternância (filtro ligado/desligado). Vai direto para o `<button>`. */
  'aria-pressed'?: boolean;
}) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={cn(
        buttonVariants({ variant: KIND[kind], size: small ? 'sm' : 'default' }),
        className,
      )}
      {...rest}
    >
      {icon !== undefined && <Icon name={icon} size={small ? 14 : 16} />}
      {children}
    </button>
  );
}

/** Botão redondo só com ícone. `label` é obrigatório: vira `aria-label` e `title`. */
export function IconBtn({
  icon,
  label,
  onClick,
  ink = 'muted',
  disabled = false,
  className,
}: {
  icon: IconName;
  label: string;
  onClick?: () => void;
  ink?: 'muted' | 'danger' | 'text';
  disabled?: boolean;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      disabled={disabled}
      className={cn(
        buttonVariants({ variant: 'outline', size: 'icon-sm' }),
        ink === 'danger'
          ? 'text-destructive'
          : ink === 'text'
            ? 'text-foreground'
            : 'text-muted-foreground',
        className,
      )}
    >
      <Icon name={icon} size={15} />
    </button>
  );
}
